/**
 * The one `auth-session` BroadcastChannel every tab of this app shares.
 *
 * It started as session-alive signalling for "no remember me" and now also
 * carries refresh-token rotation, so both features speak through one object:
 * two BroadcastChannel objects in the same tab would echo each other's posts.
 */

/**
 * `alive` / `dead` / `query` stay bare strings on the wire. During a deploy a
 * tab running the previous build is still listening for exactly those three
 * values, and an object would read as an unknown message to it.
 */
export type AuthChannelMessage =
  | 'alive'
  | 'dead'
  | 'query'
  /** This tab is about to POST the refresh token. Peers hold off. */
  | { type: 'refresh-started' }
  /** This tab rotated the pair. Peers must adopt it: theirs is now blacklisted. */
  | { type: 'refresh-succeeded'; access: string; refresh: string | null }

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('auth-session') : null

export function postAuthMessage(message: AuthChannelMessage): void {
  channel?.postMessage(message)
}

/** Subscribe to messages from OTHER tabs. Returns the unsubscribe function. */
export function onAuthMessage(handler: (message: AuthChannelMessage) => void): () => void {
  if (!channel) {
    return () => {}
  }
  const listener = (event: MessageEvent<AuthChannelMessage>): void => handler(event.data)
  channel.addEventListener('message', listener)
  return () => channel.removeEventListener('message', listener)
}
